import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Target, Sparkles, TrendingUp, CheckCircle, ArrowRight, Plus, X, AlertCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { storage } from '@/lib/storage';
import { api } from '@/lib/api';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { LoadingOverlay } from '@/components/LoadingOverlay';
import { RateLimitErrorDisplay } from '@/components/RateLimitError';
import { RateLimitError } from '@/lib/errors';
import { tiktok } from '@/services/tiktok';
import { funnelAnalytics } from '@/services/funnel-analytics';

// Popular goal suggestions for one-click selection
const GOAL_SUGGESTIONS = [
  { emoji: '💪', text: 'Get in shape and lose weight' },
  { emoji: '💰', text: 'Build financial security' },
  { emoji: '📈', text: 'Get promoted at work' },
  { emoji: '🚀', text: 'Start my own business' },
  { emoji: '📚', text: 'Learn a new skill' },
  { emoji: '🧘', text: 'Reduce stress and anxiety' },
  { emoji: '❤️', text: 'Improve my relationships' },
  { emoji: '✍️', text: 'Write a book or create content' },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const [goals, setGoals] = useState<string[]>(['']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [rateLimitError, setRateLimitError] = useState<RateLimitError | null>(null);

  useEffect(() => {
    // Track TikTok ViewContent for productivity landing page
    tiktok.trackViewContent({
      contentId: 'productivity-landing',
      contentName: 'Productivity Goals Landing Page',
      contentType: 'page',
    });

    // Track funnel step: Landing viewed
    funnelAnalytics.trackLandingViewed();

    // Track page view if analytics is configured
    const analyticsId = import.meta.env.VITE_ANALYTICS_ID;
    if (analyticsId) {
      // Analytics tracking would go here
    }
  }, []);

  const handleGoalChange = (index: number, value: string) => {
    const newGoals = [...goals];
    newGoals[index] = value;
    setGoals(newGoals);
  };

  const handleAddGoal = () => {
    if (goals.length < 7) {
      setGoals([...goals, '']);
    }
  };

  const handleRemoveGoal = (index: number) => {
    if (goals.length > 1) {
      setGoals(goals.filter((_, i) => i !== index));
    }
  };

  const handleAddSuggestion = (suggestion: string) => {
    // Check if already added
    if (goals.some(g => g.toLowerCase() === suggestion.toLowerCase())) {
      return;
    }

    // Find first empty slot or add new
    const emptyIndex = goals.findIndex(g => g.trim() === '');
    if (emptyIndex !== -1) {
      const newGoals = [...goals];
      newGoals[emptyIndex] = suggestion;
      setGoals(newGoals);
    } else if (goals.length < 7) {
      setGoals([...goals, suggestion]);
    }
  };

  const isGoalSelected = (suggestion: string) => {
    return goals.some(g => g.toLowerCase() === suggestion.toLowerCase());
  };

  const handleContinue = async () => {
    setError('');
    setRateLimitError(null);

    const filledGoals = goals.filter(g => g.trim().length >= 3);

    if (filledGoals.length < 1) {
      setError('Please enter at least 1 goal to get started');
      return;
    }

    // Track TikTok ClickButton for goals submission
    tiktok.trackClickButton({
      contentId: 'analyze-goals-submit',
      contentName: 'Analyze Goals - Start',
      contentType: 'form_submission',
    });

    try {
      setLoading(true);
      const response = await api.analyzeGoals(filledGoals);

      // Track funnel step: Goals submitted
      funnelAnalytics.trackGoalsSubmitted(filledGoals.length);

      // Save to storage
      storage.setGoals(filledGoals);
      storage.setQuestions(response.questions);
      storage.setAnswers({});
      storage.setCurrentStep('questions');

      // Navigate to questions
      navigate('/questions');
    } catch (err) {
      console.error('Failed to analyze goals:', err);

      if (err instanceof RateLimitError) {
        setRateLimitError(err);
      } else if (err instanceof Error) {
        setError(err.message);
      } else if (typeof err === 'object' && err !== null) {
        // Handle cases where err is an object but not an Error instance
        setError(JSON.stringify(err));
      } else {
        setError('Failed to analyze goals. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const filledGoalsCount = goals.filter(g => g.trim().length >= 3).length;
  const canContinue = filledGoalsCount >= 1 && !loading;

  return (
    <div>
      {/* Loading overlay with fun messages */}
      <LoadingOverlay
        isLoading={loading}
        messages={[
          'Analyzing your goals...',
          'Generating smart questions...',
          'Finding patterns...',
          'Preparing insights...',
          'Almost ready...',
        ]}
      />

      {/* Hero Section */}
      <section className="section bg-gradient-to-br from-primary-50 to-blue-50 min-h-[calc(100vh-80px)] flex items-center">
        <div className="container-tight">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-8"
          >
            {/* Progress indicator */}
            <div className="flex items-center justify-center gap-2 mb-6">
              <div className="flex items-center gap-1">
                <div className="w-8 h-8 rounded-full bg-primary-600 text-white flex items-center justify-center text-sm font-bold">1</div>
                <div className="w-12 h-1 bg-gray-300 rounded"></div>
                <div className="w-8 h-8 rounded-full bg-gray-300 text-gray-500 flex items-center justify-center text-sm font-bold">2</div>
                <div className="w-12 h-1 bg-gray-300 rounded"></div>
                <div className="w-8 h-8 rounded-full bg-gray-300 text-gray-500 flex items-center justify-center text-sm font-bold">3</div>
              </div>
              <span className="text-sm text-gray-600 ml-3">~2 min total</span>
            </div>

            <h1 className="heading">
              What's the <span className="gradient-text">ONE thing</span> you want to achieve?
            </h1>
            <p className="subheading">
              Tell us your goals and get a free personalized plan in under 2 minutes
            </p>
          </motion.div>

          {/* Inline Goal Input Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="card max-w-2xl mx-auto"
          >
            {/* Quick-select suggestions */}
            <div className="mb-6">
              <p className="text-sm text-gray-600 mb-3">Quick start — click to add:</p>
              <div className="flex flex-wrap gap-2">
                {GOAL_SUGGESTIONS.map((suggestion) => (
                  <button
                    key={suggestion.text}
                    onClick={() => handleAddSuggestion(suggestion.text)}
                    disabled={isGoalSelected(suggestion.text)}
                    className={`px-3 py-2 rounded-full text-sm font-medium transition-all ${
                      isGoalSelected(suggestion.text)
                        ? 'bg-primary-100 text-primary-700 border-2 border-primary-500'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border-2 border-transparent'
                    }`}
                  >
                    {suggestion.emoji} {suggestion.text}
                  </button>
                ))}
              </div>
            </div>

            <div className="border-t border-gray-200 pt-6 mb-6">
              <p className="text-sm text-gray-600 mb-3">Or type your own:</p>
            </div>

            {/* Error Display */}
            {rateLimitError && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6"
              >
                <RateLimitErrorDisplay error={rateLimitError} />
              </motion.div>
            )}

            {error && !rateLimitError && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="error-message mb-6 flex items-start gap-3"
              >
                <AlertCircle size={20} className="flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </motion.div>
            )}

            <div className="space-y-3 mb-6">
              {goals.map((goal, index) => (
                <div key={index} className="flex gap-2">
                  <div className="flex-grow">
                    <input
                      type="text"
                      value={goal}
                      onChange={(e) => handleGoalChange(index, e.target.value)}
                      placeholder={index === 0 ? "e.g., Get promoted at work this year" : "Add another goal..."}
                      className="input"
                      autoFocus={index === 0}
                    />
                  </div>
                  {goals.length > 1 && (
                    <button
                      onClick={() => handleRemoveGoal(index)}
                      className="p-3 text-gray-400 hover:text-error-600 transition-colors"
                      aria-label="Remove goal"
                    >
                      <X size={20} />
                    </button>
                  )}
                </div>
              ))}
            </div>

            {goals.length < 7 && (
              <button
                onClick={handleAddGoal}
                className="btn-secondary w-full mb-6 flex items-center justify-center gap-2"
              >
                <Plus size={20} />
                Add Another Goal {filledGoalsCount >= 1 && '(optional)'}
              </button>
            )}

            <button
              onClick={handleContinue}
              disabled={!canContinue}
              className="btn-primary btn-large w-full"
            >
              {loading ? (
                <LoadingSpinner inline />
              ) : canContinue ? (
                <>
                  Get My Free Plan
                  <ArrowRight className="ml-2" size={20} />
                </>
              ) : (
                'Enter a goal to get started'
              )}
            </button>

            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="flex flex-wrap justify-center items-center gap-4 md:gap-8 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <CheckCircle className="text-success-600" size={18} />
                  <span>100% Free</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="text-success-600" size={18} />
                  <span>No sign-up</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="text-success-600" size={18} />
                  <span>Results in 2 min</span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Social Proof - Moved closer to form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-8 max-w-2xl mx-auto"
          >
            <div className="flex items-center justify-center gap-6 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <div className="text-yellow-400">★★★★★</div>
                <span>4.9/5 rating</span>
              </div>
              <div className="hidden md:block w-px h-4 bg-gray-300"></div>
              <span>Trusted by 10,000+ users</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Sample Report Preview */}
      <section className="section bg-white">
        <div className="container-narrow">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Here's What You'll Get
            </h2>
            <p className="text-lg text-gray-600">
              A personalized productivity plan with prioritized goals and actionable steps
            </p>
          </div>

          {/* Sample Report Preview Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative max-w-2xl mx-auto"
          >
            <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
              {/* Sample Report Header */}
              <div className="bg-gradient-to-r from-primary-600 to-blue-600 text-white p-6 text-center">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
                  <CheckCircle size={24} />
                </div>
                <h3 className="text-xl font-bold">Your Productivity Plan</h3>
              </div>

              {/* Sample Content */}
              <div className="p-6 space-y-4">
                {/* Sample Goal */}
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-primary-600 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0">1</div>
                  <div>
                    <div className="font-semibold text-gray-900">Get promoted at work</div>
                    <div className="text-sm text-gray-500">Priority: 95/100 • Est: 6 months</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-primary-500 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0">2</div>
                  <div>
                    <div className="font-semibold text-gray-900">Build financial security</div>
                    <div className="text-sm text-gray-500">Priority: 88/100 • Est: 12 months</div>
                  </div>
                </div>

                {/* Blurred preview area */}
                <div className="relative mt-4">
                  <div className="blur-sm pointer-events-none">
                    <div className="border-t pt-4">
                      <div className="text-sm font-semibold text-gray-700 mb-2">Daily Habits</div>
                      <div className="space-y-1 text-sm text-gray-600">
                        <div className="flex items-center gap-2"><CheckCircle size={14} className="text-green-500" /> Morning goal review</div>
                        <div className="flex items-center gap-2"><CheckCircle size={14} className="text-green-500" /> 30 min skill building</div>
                      </div>
                    </div>
                    <div className="border-t pt-4 mt-4">
                      <div className="text-sm font-semibold text-gray-700 mb-2">Your Timeline</div>
                      <div className="space-y-1 text-sm text-gray-600">
                        <div>Phase 1: Foundation (Weeks 1-4)</div>
                        <div>Phase 2: Growth (Months 2-3)</div>
                      </div>
                    </div>
                  </div>

                  {/* Overlay prompt */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="bg-white/95 px-6 py-3 rounded-full shadow-lg border">
                      <span className="text-primary-600 font-semibold">Enter your goals to see your full plan</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Decorative elements */}
            <div className="absolute -top-4 -right-4 w-24 h-24 bg-primary-100 rounded-full opacity-50 -z-10"></div>
            <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-blue-100 rounded-full opacity-50 -z-10"></div>
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="section bg-gray-50">
        <div className="container-narrow">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What's Included
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="card-simple"
            >
              <Target className="text-primary-600 mb-4" size={40} />
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Priority Rankings
              </h3>
              <p className="text-gray-600">
                Discover which goals to focus on first based on impact and dependencies.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="card-simple"
            >
              <TrendingUp className="text-primary-600 mb-4" size={40} />
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Custom Timeline
              </h3>
              <p className="text-gray-600">
                Get a realistic timeline with phases and milestones.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="card-simple"
            >
              <Sparkles className="text-primary-600 mb-4" size={40} />
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Actionable Habits
              </h3>
              <p className="text-gray-600">
                Daily and weekly habits designed for your goals.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Social Proof */}
      <section className="section bg-white">
        <div className="container-narrow">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Join Thousands Getting Results
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="bg-white rounded-xl p-6 shadow-lg"
            >
              <div className="text-yellow-400 text-xl mb-3">⭐⭐⭐⭐⭐</div>
              <p className="text-gray-700 mb-4">
                "This tool helped me realize I was focusing on the wrong goals. The prioritization
                was spot-on and saved me months of wasted effort."
              </p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary-200 rounded-full flex items-center justify-center text-primary-700 font-bold">
                  J
                </div>
                <div>
                  <div className="font-semibold text-gray-900">Jessica T.</div>
                  <div className="text-sm text-gray-600">Entrepreneur</div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-xl p-6 shadow-lg"
            >
              <div className="text-yellow-400 text-xl mb-3">⭐⭐⭐⭐⭐</div>
              <p className="text-gray-700 mb-4">
                "The personalized questions made me think deeply about my goals. The advice was
                incredibly practical and immediately actionable."
              </p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary-200 rounded-full flex items-center justify-center text-primary-700 font-bold">
                  M
                </div>
                <div>
                  <div className="font-semibold text-gray-900">Marcus R.</div>
                  <div className="text-sm text-gray-600">Product Manager</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section bg-primary-600 text-white">
        <div className="container-tight text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-block bg-white/20 text-white px-4 py-2 rounded-full text-sm font-semibold mb-4">
              ✨ 100% FREE FOREVER
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready to Achieve Your Goals Faster?
            </h2>
            <p className="text-xl text-primary-100 mb-8">
              Join thousands who've already gotten their free personalized productivity plan
            </p>
            <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="btn-large bg-white text-primary-600 hover:bg-gray-100 group">
              Start Your Free Plan Now
              <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
            </button>
            <p className="mt-4 text-primary-200 text-sm">
              No credit card • No sign-up • No strings attached
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
